'use client';

import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';
import { useEffect, useRef } from 'react';
import { authCodeGroupStyle, authCodeSlotStyle } from '@/lib/auth/auth-control-styles';
import { cn } from '@/lib/utils';

interface VerificationCodeInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onComplete: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

// Default input for app-owned six-digit verification challenges.
export const VerificationCodeInput = ({ id, value, onChange, onComplete, disabled, invalid, describedBy }: VerificationCodeInputProps) => {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!disabled) {
      input.current?.focus();
    }
  }, [disabled]);

  return <OTPInput
    ref={input}
    id={id}
    name="code"
    value={value}
    onChange={onChange}
    onComplete={onComplete}
    maxLength={6}
    minLength={6}
    pattern={REGEXP_ONLY_DIGITS}
    pasteTransformer={(text) => { return text.replace(/[\s-]/g, ''); }}
    inputMode="numeric"
    autoComplete="one-time-code"
    textAlign="center"
    autoFocus
    required
    disabled={disabled}
    aria-invalid={invalid || undefined}
    aria-describedby={describedBy}
    containerClassName="flex justify-center py-1 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50"
    render={({ slots }) => {
      return <div aria-hidden="true" style={authCodeGroupStyle} className="flex items-center">
        {slots.map((slot, index) => {
          return <div
            key={index}
            style={authCodeSlotStyle}
            data-active={slot.isActive}
            className={cn(
              'relative flex items-center justify-center border-input shadow-sm transition-colors',
              slot.isActive && 'border-primary ring-2 ring-ring ring-offset-2 ring-offset-background',
              invalid && 'border-destructive',
            )}
          >
            <span>{slot.char}</span>
            {slot.hasFakeCaret && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className="h-5 w-px animate-pulse bg-foreground motion-reduce:animate-none" /></div>}
          </div>;
        })}
      </div>;
    }}
  />;
};
